"use client"

import { useState, useEffect, useContext } from "react"
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { AuthContext } from "../context/AuthContext"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import "./FarmManagement.css"

const FarmManagement = () => {
  const { user } = useContext(AuthContext)
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const navigate = useNavigate()

  const [farms, setFarms] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("createdAt")
  const [sortOrder, setSortOrder] = useState("desc")
  const [activeTab, setActiveTab] = useState("active") // "active" or "inactive"

  useEffect(() => {
    fetchFarms()
  }, [sortBy, sortOrder, activeTab])

  const fetchFarms = async () => {
    try {
      setLoading(true)
      const response = await axios.get(`${API_URL}/api/farms?active=${activeTab === "active"}`)
      setFarms(response.data)
      setLoading(false)
    } catch (err) {
      setError("Failed to load farms")
      setLoading(false)
      console.error(err)
    }
  }

  const handleSetFarmStatus = async (farmId, farmName, active) => {
    const action = active ? "activate" : "deactivate"

    if (!window.confirm(t(`Are you sure you want to ${action} "${farmName}"?`))) {
      return
    }

    try {
      await axios.patch(`${API_URL}/api/farms/${farmId}/status`, { active })
      showNotification(t(`Farm ${active ? "activated" : "deactivated"} successfully`), "success")
      fetchFarms() // Refresh the list
    } catch (err) {
      console.error(`Error ${action}ing farm:`, err)
      showNotification(err.response?.data?.msg || `Failed to ${action} farm`, "error")
    }
  }

  const handleDeleteFarm = async (farmId, farmName) => {
    if (
      !window.confirm(t(`Are you sure you want to permanently delete "${farmName}"? This action cannot be undone.`))
    ) {
      return
    }

    try {
      await axios.delete(`${API_URL}/api/farms/${farmId}`)
      showNotification(t("Farm deleted successfully"), "success")
      fetchFarms() // Refresh the list
    } catch (err) {
      console.error("Error deleting farm:", err)
      showNotification(err.response?.data?.msg || "Failed to delete farm", "error")
    }
  }

  const filteredFarms = farms.filter(
    (farm) =>
      farm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farm.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farm.district.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      farm.country.name.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const sortedFarms = [...filteredFarms].sort((a, b) => {
    let aValue = a[sortBy]
    let bValue = b[sortBy]

    if (sortBy === "district") {
      aValue = a.district.name
      bValue = b.district.name
    } else if (sortBy === "country") {
      aValue = a.country.name
      bValue = b.country.name
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1
    } else {
      return aValue < bValue ? 1 : -1
    }
  })

  if (loading) {
    return (
      <div className="farm-management-loading">
        <div className="spinner"></div>
        <p>{t("Loading farms...")}</p>
      </div>
    )
  }

  return (
    <div className="farm-management-container" dir={direction}>
      <div className="farm-management-header">
        <div className="header-content">
          <h1>{t("Farm Management")}</h1>
          <p className="header-subtitle">
            {t("Manage all your farms in one place")} • {farms.length}{" "}
            {activeTab === "active" ? t("active farms") : t("inactive farms")}
          </p>
        </div>
        <div className="header-actions">
          <Link to="/add-farm" className="add-farm-button">
            <i className="fas fa-plus"></i> {t("Add New Farm")}
          </Link>
        </div>
      </div>

      <div className="farm-tabs">
        <button
          className={`tab-button ${activeTab === "active" ? "active" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          <i className="fas fa-check-circle"></i> {t("Active Farms")}
        </button>
        <button
          className={`tab-button ${activeTab === "inactive" ? "active" : ""}`}
          onClick={() => setActiveTab("inactive")}
        >
          <i className="fas fa-times-circle"></i> {t("Inactive Farms")}
        </button>
      </div>

      <div className="farm-management-controls">
        <div className="search-container">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder={t("Search farms by name, code, location...")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="sort-controls">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="name">{t("Sort by Name")}</option>
            <option value="code">{t("Sort by Code")}</option>
            <option value="area">{t("Sort by Area")}</option>
            <option value="district">{t("Sort by District")}</option>
            <option value="country">{t("Sort by Country")}</option>
            <option value="createdAt">{t("Sort by Date Created")}</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="sort-order-button"
            title={t(sortOrder === "asc" ? "Ascending" : "Descending")}
          >
            <i className={`fas fa-sort-${sortOrder === "asc" ? "up" : "down"}`}></i>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          <p>{t(error)}</p>
        </div>
      )}

      {sortedFarms.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-seedling empty-icon"></i>
          <h3>
            {searchTerm ? t("No farms found") : activeTab === "active" ? t("No active farms") : t("No inactive farms")}
          </h3>
          <p>
            {searchTerm
              ? t("Try adjusting your search terms")
              : activeTab === "active"
                ? t("Create your first farm to get started with project management")
                : t("Deactivated farms will appear here")}
          </p>
          {!searchTerm && activeTab === "active" && (
            <Link to="/add-farm" className="btn btn-primary">
              <i className="fas fa-plus"></i> {t("Add Your First Farm")}
            </Link>
          )}
          {!searchTerm && activeTab === "inactive" && farms.length === 0 && (
            <button onClick={() => setActiveTab("active")} className="btn btn-secondary">
              <i className="fas fa-arrow-left"></i> {t("View Active Farms")}
            </button>
          )}
        </div>
      ) : (
        <div className="farms-grid">
          {sortedFarms.map((farm) => (
            <div key={farm._id} className={`farm-card ${activeTab === "inactive" ? "inactive-farm" : ""}`}>
              <div className="farm-card-header">
                <div className="farm-icon">
                  <i className="fas fa-tractor"></i>
                </div>
                <div className="farm-basic-info">
                  <h3 className="farm-name">{farm.name}</h3>
                  <p className="farm-code">{farm.code}</p>
                </div>
                <div className="farm-actions-dropdown">
                  <button className="dropdown-toggle">
                    <i className="fas fa-ellipsis-v"></i>
                  </button>
                  <div className="dropdown-menu">
                    <Link to={`/farms/${farm._id}`} className="dropdown-item">
                      <i className="fas fa-eye"></i> {t("View Details")}
                    </Link>
                    <Link to={`/farms/${farm._id}/edit`} className="dropdown-item">
                      <i className="fas fa-edit"></i> {t("Edit Farm")}
                    </Link>
                    {activeTab === "active" && (
                      <Link to={`/add-project?farmId=${farm._id}`} className="dropdown-item">
                        <i className="fas fa-plus"></i> {t("Add Project")}
                      </Link>
                    )}
                    {activeTab === "active" ? (
                      <button
                        onClick={() => handleSetFarmStatus(farm._id, farm.name, false)}
                        className="dropdown-item warning-item"
                      >
                        <i className="fas fa-times-circle"></i> {t("Deactivate Farm")}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleSetFarmStatus(farm._id, farm.name, true)}
                          className="dropdown-item success-item"
                        >
                          <i className="fas fa-check-circle"></i> {t("Reactivate Farm")}
                        </button>
                        <button
                          onClick={() => handleDeleteFarm(farm._id, farm.name)}
                          className="dropdown-item delete-item"
                        >
                          <i className="fas fa-trash"></i> {t("Delete Permanently")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="farm-card-body">
                <div className="farm-location">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>
                    {farm.district|| "not available"}, {farm.country}
                  </span>
                </div>

                <div className="farm-stats">
                  <div className="stat-item">
                    <span className="stat-label">{t("Total Area")}</span>
                    <span className="stat-value">{farm.area.toLocaleString()} m²</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">{t("Available")}</span>
                    <span className="stat-value">{farm.availableArea.toLocaleString()} m²</span>
                  </div>
                </div>

                <div className="farm-coordinates">
                  <i className="fas fa-compass"></i>
                  <span>
                    {farm.location.coordinates[0].toFixed(4)}, {farm.location.coordinates[1].toFixed(4)}
                  </span>
                </div>

                <div className="farm-created">
                  <i className="fas fa-calendar"></i>
                  <span>
                    {t("Created")}: {new Date(farm.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="farm-card-footer">
                <Link to={`/farms/${farm._id}`} className="view-farm-button">
                  <i className="fas fa-eye"></i> {t("View Farm")}
                </Link>
                {activeTab === "active" ? (
                  <Link to={`/add-project?farmId=${farm._id}`} className="add-project-button">
                    <i className="fas fa-plus"></i> {t("Add Project")}
                  </Link>
                ) : (
                  <button onClick={() => handleSetFarmStatus(farm._id, farm.name, true)} className="reactivate-button">
                    <i className="fas fa-redo"></i> {t("Reactivate")}
                  </button>
                )}
              </div>

              {activeTab === "inactive" && (
                <div className="inactive-badge">
                  <i className="fas fa-times-circle"></i> {t("Inactive")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default FarmManagement
